import type { ScaleBand, ScaleLinear, ScaleTime } from "d3-scale";

export type XScaleKind = "time" | "linear" | "band";

export type XRawValue = Date | number | string;

export type AnyXScale =
  | ScaleTime<number, number>
  | ScaleLinear<number, number>
  | ScaleBand<string>;

export type ContinuousXScale =
  | ScaleTime<number, number>
  | ScaleLinear<number, number>;

export function isBandScale(
  scale: AnyXScale,
): scale is ScaleBand<string> {
  return typeof (scale as ScaleBand<string>).bandwidth === "function";
}

/**
 * Half a band, or zero on a continuous scale. A band scale resolves to the
 * band's left edge, while the axis ticks and the tooltip crosshair both centre
 * on it — so anything that draws data has to add this to line up with them.
 */
export function bandCenterOffset(scale: unknown): number {
  // Takes `unknown` on purpose: several call sites receive the scale narrowed
  // to a plain accessor function, and the band check is a duck-type probe for
  // `bandwidth` either way.
  const banded = scale as { bandwidth?: () => number } | null | undefined;
  return typeof banded?.bandwidth === "function" ? banded.bandwidth() / 2 : 0;
}

export function isContinuousScale(
  scale: AnyXScale,
): scale is ContinuousXScale {
  return typeof (scale as ContinuousXScale).invert === "function";
}

export function xValueOf(value: XRawValue): number {
  if (typeof value === "string") {
    return Number.NaN;
  }
  return value.valueOf();
}

export function formatXLabel(
  value: XRawValue,
  kind: XScaleKind,
  locale = "en-US",
): string {
  if (kind === "time" && value instanceof Date) {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "2-digit",
    }).format(value);
  }
  return String(value);
}
